import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, Star, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { useShop } from "../../context/ShopContext";
import { img, inr } from "../../lib/api";

export const Price = ({ mrp, price, pct, size = "base" }) => (
  <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
    <span className={`font-semibold text-[#1B3B2B] ${size === "lg" ? "text-2xl" : "text-base"}`} data-testid="price-selling">{inr(price)}</span>
    {mrp > price && <span className="text-xs sm:text-sm text-stone-400 line-through" data-testid="price-mrp">MRP {inr(mrp)}</span>}
    {pct > 0 && <span className="text-xs font-medium text-[#B91C1C]" data-testid="price-discount">{Math.round(pct)}% off</span>}
  </div>
);

export const Rating = ({ avg, count }) =>
  count > 0 ? (
    <div className="flex items-center gap-1 text-xs text-stone-500">
      <Star className="w-3.5 h-3.5 fill-[#C69C5C] text-[#C69C5C]" /> {avg} <span>({count})</span>
    </div>
  ) : null;

function QuickView({ p, open, onClose }) {
  const { addToCart } = useShop();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[#FFFDF9] p-0 overflow-hidden" data-testid="quick-view-dialog">
        <DialogTitle className="sr-only">{p.name}</DialogTitle>
        <div className="grid sm:grid-cols-2">
          <img src={img(p.images?.[0]?.url)} alt={p.images?.[0]?.alt || p.name} className="w-full aspect-square object-cover bg-[#F4EFE6]" />
          <div className="p-6 space-y-4">
            {p.is_demo && <span className="text-[10px] uppercase tracking-widest bg-[#F4EFE6] px-2 py-1 rounded-full">Demo product</span>}
            <h3 className="font-serif text-3xl text-[#1B3B2B] leading-tight">{p.name}</h3>
            <p className="text-sm text-stone-600">{p.short_description}</p>
            <Price mrp={p.mrp} price={p.price} pct={p.discount_percent} size="lg" />
            <div className="flex gap-3 pt-2">
              <button disabled={p.is_demo || p.stock <= 0} onClick={() => { addToCart(p, p.variants?.[0] || null); onClose(); }} data-testid="quick-view-add-to-cart"
                className="flex-1 bg-[#1B3B2B] text-[#FBF8F3] rounded-full py-3 text-sm hover:bg-[#2C523A] transition-colors disabled:opacity-50">
                {p.is_demo ? "Not available" : p.stock > 0 ? "Add to Cart" : "Out of stock"}
              </button>
              <Link to={`/product/${p.slug}`} onClick={onClose} className="px-5 py-3 rounded-full border border-[#1B3B2B] text-sm text-[#1B3B2B] hover:bg-[#E2EBE2] transition-colors" data-testid="quick-view-details-link">Details</Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductCard({ p }) {
  const { addToCart, wishlist, toggleWish } = useShop();
  const [qv, setQv] = useState(false);
  const wished = wishlist.includes(p.id);
  return (
    <div className="group relative flex flex-col bg-[#FFFDF9] border border-[#E8E0D5] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow] duration-300" data-testid={`product-card-${p.slug}`}>
      <Link to={`/product/${p.slug}`} className="relative block aspect-[4/5] bg-[#F4EFE6] overflow-hidden">
        <img src={img(p.images?.[0]?.url)} alt={p.images?.[0]?.alt || p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
        {p.discount_percent > 0 && <span className="absolute top-3 left-3 text-[11px] font-medium bg-[#FFFDF9] text-[#B91C1C] px-2.5 py-1 rounded-full">{Math.round(p.discount_percent)}% OFF</span>}
        {(p.is_demo || p.stock <= 0) && <span className="absolute bottom-3 left-3 text-[11px] bg-stone-800 text-white px-2.5 py-1 rounded-full" data-testid={`unavailable-${p.slug}`}>{p.is_demo ? "Not available" : "Out of stock"}</span>}
      </Link>
      <button onClick={() => toggleWish(p.id)} aria-label="Wishlist" data-testid={`wishlist-toggle-${p.slug}`} className="absolute top-3 right-3 p-2 rounded-full bg-[#FFFDF9]/90 hover:bg-white transition-colors">
        <Heart className={`w-4 h-4 ${wished ? "fill-[#D48B68] text-[#D48B68]" : "text-[#1B3B2B]"}`} />
      </button>
      <div className="p-3 sm:p-4 flex flex-col gap-2 flex-1">
        <Rating avg={p.rating_avg} count={p.rating_count} />
        <Link to={`/product/${p.slug}`} className="text-sm sm:text-base font-semibold text-stone-900 tracking-tight leading-snug hover:text-[#2C523A]">{p.name}</Link>
        <p className="text-xs text-stone-500 line-clamp-2 hidden sm:block">{p.short_description}</p>
        <div className="mt-auto pt-1"><Price mrp={p.mrp} price={p.price} pct={p.discount_percent} /></div>
        <div className="flex gap-2">
          <button disabled={p.is_demo || p.stock <= 0} onClick={() => addToCart(p, p.variants?.[0] || null)} data-testid={`add-to-cart-${p.slug}`}
            className="flex-1 flex items-center justify-center gap-1.5 bg-[#1B3B2B] text-[#FBF8F3] rounded-full py-2.5 text-xs sm:text-sm hover:bg-[#2C523A] transition-colors disabled:opacity-40">
            <ShoppingBag className="w-4 h-4" /> <span>Add</span>
          </button>
          <button onClick={() => setQv(true)} aria-label="Quick view" data-testid={`quick-view-${p.slug}`} className="px-3 rounded-full border border-[#E8E0D5] hover:bg-[#E2EBE2] transition-colors"><Eye className="w-4 h-4 text-[#1B3B2B]" /></button>
        </div>
      </div>
      <QuickView p={p} open={qv} onClose={() => setQv(false)} />
    </div>
  );
}
